import { defineStore } from "pinia";
import { InteractionStatus2Enum, AllThreadStatusKeys } from "@/utils/constants.js";
import { useUserStore } from "./user";
import api from "@/api";

function defaultStatusCountMap() {
  const statusCounts = {};
  for (const key in InteractionStatus2Enum) {
    const status = InteractionStatus2Enum[key];
    statusCounts[status] = 0;
  }
  return statusCounts;
}

function defaultThreadStatusCountMap() {
  const map = {};
  for (const key of AllThreadStatusKeys) {
    map[key] = 0;
  }
  return map;
}

function defaultInteractionCounts() {
  return {
    all_tickets: {
      page_enum: "ALL_TICKETS",
      total_count: 0,
      active_count: 0,
      status_count_map: defaultStatusCountMap(),
      thread_status_count_map: defaultThreadStatusCountMap(),
    },
    my_tickets: {
      page_enum: "MY_TICKETS",
      total_count: 0,
      active_count: 0,
      status_count_map: defaultStatusCountMap(),
      thread_status_count_map: defaultThreadStatusCountMap(),
    },
    my_teams: {
      page_enum: "MY_TEAMS",
      total_count: 0,
      active_count: 0,
      status_count_map: defaultStatusCountMap(),
      thread_status_count_map: defaultThreadStatusCountMap(),
    },
    my_departments: {
      page_enum: "MY_DEPARTMENTS",
      total_count: 0,
      active_count: 0,
      status_count_map: defaultStatusCountMap(),
      thread_status_count_map: defaultThreadStatusCountMap(),
    },
    all_soft_alerts: {
      page_enum: "ALL_SOFT_ALERTS",
      total_count: 0,
      active_count: 0,
      status_count_map: defaultStatusCountMap(),
      thread_status_count_map: defaultThreadStatusCountMap(),
    },
    my_soft_alerts: {
      page_enum: "MY_SOFT_ALERTS",
      total_count: 0,
      active_count: 0,
      status_count_map: defaultStatusCountMap(),
      thread_status_count_map: defaultThreadStatusCountMap(),
    },
    all_interactions: {
      page_enum: "ALL_INTERACTIONS",
      total_count: 0,
      active_count: 0,
      status_count_map: defaultStatusCountMap(),
      thread_status_count_map: defaultThreadStatusCountMap(),
    },
    va_interactions: {
      page_enum: "VA_INTERACTIONS",
      total_count: 0,
      active_count: 0,
      status_count_map: defaultStatusCountMap(),
      thread_status_count_map: defaultThreadStatusCountMap(),
    },
    user_tickets: {
      page_enum: "USER_TICKETS",
      total_count: 0,
      active_count: 0,
      status_count_map: defaultStatusCountMap(),
      thread_status_count_map: defaultThreadStatusCountMap(),
    },
  };
}

function defaultTrainingCounts() {
  return {
    my_trainings: 0,
    for_my_review: 0,
    live: 0,
  };
}

export const useCountsStore = defineStore("counts", {
  state: () => ({
    interactionCounts: defaultInteractionCounts(),
    trainingCounts: defaultTrainingCounts(),
    /** User inbox counts: { "Open": n, "Awaiting Your Reply": n, ... } */
    userCategoryCounts: {},
  }),
  actions: {
    async fetchInteractionCounts() {
      try {
        const userStore = useUserStore()
        const activeOrganization = userStore.activeOrganization ?? null
        const result = await api.counts.fetchInteractionPageCounts(activeOrganization);
        this.interactionCounts = Object.assign(result.data);
      } catch (error) {
        console.error("Error fetching interaction counts:", error);
      }
    },
    async fetchTrainingCounts() {
      try {
        const userStore = useUserStore()
        const activeOrganization = userStore.activeOrganization ?? null
        const result = await api.counts.fetchTrainingPageCounts(activeOrganization);
        this.trainingCounts = result.data;
      } catch (error) {
        console.error("Error fetching training counts:", error);
      }
    },
    async fetchUserCategoryCounts() {
      // Hardcoded for local FE testing when backend isn't reachable – comment back in if needed
      // const HARDCODED_USER_CATEGORY_COUNTS = {
      //   Open: 12,
      //   "Awaiting Your Reply": 3,
      //   "Pending Form Submission": 2,
      //   "Closed by You": 7,
      //   "Closed by Support": 4,
      // };
      // this.userCategoryCounts = HARDCODED_USER_CATEGORY_COUNTS;
      // return;

      try {
        const userStore = useUserStore();
        const activeOrganization = userStore.activeOrganization ?? null;
        const result = await api.counts.fetchUserCategoryCounts(activeOrganization);
        this.userCategoryCounts = result.data ?? {};
      } catch (error) {
        console.error("Error fetching user category counts:", error);
      }
    },
  },
});
